import type { CollaboratorRole } from './collaborator-role.js'

/**
 * Contrats salariés : ceux que le module RH crée et fait évoluer (création, « Nouveau contrat »,
 * modification, import), et les seuls qu'ouvre `collaborator_contracts`.
 */
export const salariedContractType = ['cdi', 'cdd', 'interim'] as const
export type SalariedContractType = (typeof salariedContractType)[number]

/**
 * Types de contrat d'un collaborateur (`collaborators.contract_type`). La sous-traitance n'est pas
 * un contrat de travail : un chauffeur d'un transporteur, créé depuis la fiche logistique, sans
 * contrat ouvert ni offre −15 %, au coût horaire et à la durée hebdomadaire facultatifs (CHECK
 * `collaborators_cost_hours_unless_subcontracting`, `collaborators_subcontracting_driver_only`).
 */
export const contractType = [...salariedContractType, 'sous_traitance'] as const
export type ContractType = (typeof contractType)[number]

export const SUBCONTRACTING_CONTRACT_TYPE = 'sous_traitance' satisfies ContractType

/** Seul rôle d'un sous-traitant (CHECK `collaborators_subcontracting_driver_only`). */
export const SUBCONTRACTING_ROLE = 'chauffeur' satisfies CollaboratorRole

/** Couple rôle × contrat possible : la sous-traitance n'existe que pour un chauffeur. */
export const isPossibleRoleContract = (role: CollaboratorRole, type: ContractType): boolean =>
  type !== SUBCONTRACTING_CONTRACT_TYPE || role === SUBCONTRACTING_ROLE

export const isSalariedContractType = (type: ContractType): type is SalariedContractType =>
  type !== SUBCONTRACTING_CONTRACT_TYPE

/**
 * Un collaborateur dont on lit le type de contrat est-il un sous-traitant ? `false` quand le type
 * est illisible (absent de la fiche, faute de droit) : rien ne le désigne alors comme tel.
 */
export const isSubcontractingContractType = (type: ContractType | undefined): boolean =>
  type === SUBCONTRACTING_CONTRACT_TYPE

/**
 * Le même type, restreint aux contrats salariés : ouvrir un contrat (`collaborator_contracts`) à un
 * sous-traitant est une erreur de programmation, que l'appelant a dû refuser avant.
 */
export const salariedContractTypeOf = (type: ContractType): SalariedContractType => {
  if (!isSalariedContractType(type))
    throw new Error('Un sous-traitant n’a pas de contrat de travail')
  return type
}

/**
 * CDI, CDD : contrat de travail au matricule saisi, avec statut professionnel et dates d'entrée et
 * de début. L'intérim a un matricule généré (`AGDI`), la sous-traitance aussi (`AGDS`), sans ces
 * champs.
 */
export const hasEmploymentContract = (type: ContractType): boolean =>
  type === 'cdi' || type === 'cdd'

export const contractTypeLabel: Record<ContractType, string> = {
  cdi: 'CDI',
  cdd: 'CDD',
  interim: 'Interim',
  sous_traitance: 'Sous-traitance',
}

/** Libellés des seuls contrats salariés : les choix des formulaires RH. */
export const salariedContractTypeLabel: Record<SalariedContractType, string> = {
  cdi: contractTypeLabel.cdi,
  cdd: contractTypeLabel.cdd,
  interim: contractTypeLabel.interim,
}
